import {
  FieldPathByValue,
  FieldValues,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { AdminProfessionals } from '@platform/client';
import { MultiSelectDropdown } from '@platform/components';

/** Any field on the form holding a list of names, i.e. what a MultiSelectDropdown writes. */
type NameListField<FData extends FieldValues> = FieldPathByValue<FData, string[] | null>;

/**
 * Paired corporate client / cohort selects: selecting one or more clients narrows the cohort
 * options to that client's (or those clients') cohorts, and with no client selected every cohort
 * is offered. Both field names are supplied by the caller so any form can reuse the pairing —
 * `cohort` on the professional user search, `plannedCohorts` on the project search, etc.
 */
export const ClientCohortSelects = <FData extends FieldValues>({
  register,
  watch,
  setValue,
  clientField,
  cohortField,
  clientLabel = 'Corporate client',
  cohortLabel = 'Cohort',
}: {
  register: UseFormRegister<FData>;
  watch: UseFormWatch<FData>;
  setValue: UseFormSetValue<FData>;
  clientField: NameListField<FData>;
  cohortField: NameListField<FData>;
  clientLabel?: string;
  cohortLabel?: string;
}) => {
  const { data: cohortsByClient } = AdminProfessionals.useGetCohortsByClients();
  const clients = cohortsByClient?.clients ?? [];

  const selectedClients = (watch(clientField) as string[] | null) ?? [];
  const selectedCohorts = (watch(cohortField) as string[] | null) ?? [];

  const setNames = (field: NameListField<FData>, names: string[]) =>
    setValue(field, names as PathValue<FData, NameListField<FData>>);

  const clientOptions = clients
    .map((client) => ({ label: client.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // cohorts of the selected clients, or of every client while the client select is empty
  const clientsInScope =
    selectedClients.length > 0 ?
      clients.filter((client) => selectedClients.includes(client.name))
    : clients;
  const cohortOptions = [
    ...new Set(clientsInScope.flatMap((client) => client.cohorts).map((cohort) => cohort.name)),
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ label: name }));

  return (
    <>
      <MultiSelectDropdown
        register={() => register(clientField)}
        label={clientLabel}
        options={[...clientOptions, { label: 'None' }]}
        onSelectionChange={(items) => setNames(clientField, items)}
        selectedItems={selectedClients}
        placeholder='All'
      />
      <MultiSelectDropdown
        register={() => register(cohortField)}
        label={cohortLabel}
        options={[...cohortOptions, { label: 'None' }]}
        onSelectionChange={(items) => setNames(cohortField, items)}
        selectedItems={selectedCohorts}
        placeholder='All'
        filterable={true}
      />
    </>
  );
};
